import {useState} from "react";
import {Label} from "../../../basic/label";
import {Button} from "../../../basic/button";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {ArchivedMemberTable} from "../../../shared/tables/ArchivedMemberTable.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useGetTeamArchivedMembersByIdQuery} from "../../../../store/generated/api";

interface TeamArchivedMembersListProps {
    teamId: string;
}

export function TeamArchivedMembersList({teamId}: TeamArchivedMembersListProps) {
    const {t} = useTranslationWithKeys();
    const [pageNum, setPageNum] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const {data, isLoading, error, refetch} = useGetTeamArchivedMembersByIdQuery({
        teamId,
        page: pageNum,
        size: pageSize
    });

    const archivedMembers = data?.members || [];
    const pagination = data?.pagination;

    if (isLoading) {
        return (
            <div className="space-y-4 pt-4 border-t">
                <p className="text-muted-foreground p-3">{t('common.loading')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4 pt-4 border-t">
                <div className="p-3">
                    <p className="text-destructive">{t('admin.teams.teamManagement.errors.archivedMembersLoadError')}</p>
                    <Button variant="outline" onClick={() => refetch()} className="mt-2">
                        {t('common.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 pt-4 border-t">
            <Label>{t('admin.teams.teamManagement.archivedMembers', {count: pagination?.total ?? archivedMembers.length})}</Label>

            {archivedMembers.length === 0 ? (
                <p className="text-muted-foreground p-3">{t('admin.teams.teamManagement.noArchivedMembers')}</p>
            ) : (
                <>
                    <ArchivedMemberTable paginatedData={archivedMembers} showVisibility={true}/>

                    {pagination && (
                        <PaginationControls
                            currentPage={pageNum + 1}
                            totalPages={pagination.totalPages}
                            pageSize={pageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            paginationInfo={{
                                start: pageNum * pageSize + 1,
                                end: pageNum * pageSize + archivedMembers.length,
                                total: pagination.total
                            }}
                            onPageChange={(page) => setPageNum(page - 1)}
                            onPageSizeChange={(newPageSize) => {
                                setPageSize(newPageSize);
                                setPageNum(0);
                            }}
                            rowsPerPageLabel={t('common.rowsPerPage')}
                            previousLabel={t('common.action.previous')}
                            nextLabel={t('common.action.next')}
                        />
                    )}
                </>
            )}
        </div>
    );
}
